'use client';

/**
 * MarketTempStrip — ZONE 1 of the chain market page (market-v2 spec,
 * docs/design/market-prototype/market-v2.html).
 *
 * One slim mono row, glanceable in one second: ONE F&G gauge (the chain's V4 index
 * is the needle; GLOBAL F&G is a small slate tick on the same track plus a tiny
 * `global N` footnote — grounding as a footnote, never a second instrument), then
 * TVL / DEX vol / breadth / stables / fees as hairline-separated cells.
 *
 * Round-2 law: numbers lead. The round-1 lead band (two paragraphs + dual dials)
 * is retired — its instruments compress into this strip; its prose moves to the
 * right rail's Desk Notes.
 *
 * Real-data law: every cell renders only when its source field is present; while
 * the listeners load, a deterministic skeleton holds the slot (what SSR emits).
 * Nothing produced and nothing loading → the strip renders nothing.
 */

import React from 'react';
import type { MarketPulse } from '@/hooks/useMarketAnalysis';
import { isDataStale } from '@/hooks/useMarketAnalysis';
import type { DefiEcosystemData } from '@/types/defi';
import { formatDefiNumber } from '@/hooks/useDefiLlamaData';

// ── one hairline-separated mono cell ───────────────────────────────────────────────
function StripCell({
  k,
  v,
  change,
  extra,
  title,
}: {
  k: string;
  v: React.ReactNode;
  change?: number;
  extra?: string;
  title?: string;
}) {
  const pos = change !== undefined && change >= 0;
  return (
    <span
      title={title}
      className="flex items-baseline gap-1.5 border-l-[0.5px] border-gray-200 dark:border-divider px-3 py-2 font-mono text-[12px]"
    >
      <span className="text-[9px] uppercase tracking-[0.1em] text-slate-ds">{k}</span>
      <span className="font-bold text-gray-900 dark:text-parchment">{v}</span>
      {change !== undefined && (
        <span className={`text-[10px] font-semibold ${pos ? 'text-signal-green dark:text-signal-green-text' : 'text-alert-red dark:text-alert-red-text'}`}>
          {pos ? '↑' : '↓'}{Math.abs(change).toFixed(1)}%
        </span>
      )}
      {extra && <span className="text-[9px] uppercase tracking-[0.1em] text-slate-ds">{extra}</span>}
    </span>
  );
}

interface MarketTempStripProps {
  chainLabel: string;
  marketPulse: MarketPulse | null;
  pulseLoading: boolean;
  defiData: DefiEcosystemData | null;
  defiLoading: boolean;
}

export default function MarketTempStrip({
  chainLabel,
  marketPulse,
  pulseLoading,
  defiData,
  defiLoading,
}: MarketTempStripProps) {
  // F&G only counts while the pulse is fresh (<24h) — a stale needle is a lie.
  const pulseFresh = !!marketPulse?.timestamp && !isDataStale(marketPulse.timestamp, 24);
  const fng = pulseFresh ? marketPulse?.abstractFearGreed ?? null : null;
  const globalFng = pulseFresh ? marketPulse?.globalContext?.fearGreed ?? null : null;
  const metrics = pulseFresh ? marketPulse?.metrics ?? null : null;
  const breadthTotal = metrics
    ? metrics.gainersCount + metrics.losersCount + metrics.neutralCount
    : 0;

  const loading = (pulseLoading && !marketPulse) || (defiLoading && !defiData);
  const hasAnything = fng || globalFng || metrics || (defiData && defiData.tvl > 0);

  // Quiet state: nothing real and nothing loading → no strip (never an empty box).
  if (!loading && !hasAnything) return null;

  return (
    <div
      className="mt-4 flex flex-wrap items-stretch border-[0.5px] border-gray-200 dark:border-divider bg-white dark:bg-surface"
      aria-label={`${chainLabel} market temperature`}
    >
      {loading && !hasAnything ? (
        <span
          className="animate-pulse px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-slate-ds"
          aria-label="Loading market temperature"
        >
          Reading the {chainLabel} tape…
        </span>
      ) : (
        <>
          {/* ── the ONE gauge: chain needle + global tick on the same track ─────
              flex-wrap + min-w-0: at 390px the gauge cell must wrap inside the
              strip instead of forcing 21px of page-level horizontal overflow. */}
          {fng && (
            <span className="flex min-w-0 max-w-full flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-1.5 font-mono">
              <span className="whitespace-nowrap text-[12px] font-bold text-gray-900 dark:text-parchment">
                F&G {Math.max(0, Math.min(100, fng.value))}{' '}
                <span className="tracking-[0.06em] text-amber-600 dark:text-scout-gold">· {fng.classification}</span>
                {fng.trend && fng.trend.delta !== 0 && (
                  <span className={`ml-1.5 text-[10px] font-semibold ${fng.trend.delta < 0 ? 'text-alert-red dark:text-alert-red-text' : 'text-signal-green dark:text-signal-green-text'}`}>
                    {fng.trend.delta < 0 ? '▼' : '▲'}{Math.abs(fng.trend.delta)}
                  </span>
                )}
              </span>
              <span
                className="relative h-[5px] w-[110px] flex-shrink-0 rounded-[3px] opacity-90"
                style={{
                  background:
                    'linear-gradient(90deg, #E05C4A 0%, #8a6a3a 35%, #8A9AB0 50%, #5a9a6a 65%, #55C985 100%)',
                }}
                role="img"
                aria-label={`${chainLabel} Fear & Greed ${fng.value} (${fng.classification})${globalFng ? `, global ${globalFng.value}` : ''}`}
              >
                {globalFng && (
                  <span
                    className="absolute -top-px h-[7px] w-px bg-gray-400 dark:bg-slate-ds"
                    style={{ left: `${Math.max(0, Math.min(100, globalFng.value))}%` }}
                    title={`Global F&G ${globalFng.value} · ${globalFng.classification} (alternative.me)`}
                    aria-hidden
                  />
                )}
                <span
                  className="absolute -top-[3px] h-[11px] w-[2px] rounded-[1px] bg-gray-900 dark:bg-parchment"
                  style={{
                    left: `${Math.max(0, Math.min(100, fng.value))}%`,
                    boxShadow: '0 0 6px rgba(242,242,240,.6)',
                  }}
                  aria-hidden
                />
              </span>
              {globalFng && (
                <span
                  className="whitespace-nowrap text-[8.5px] uppercase tracking-[0.06em] text-slate-ds"
                  title={`Global Fear & Greed (alternative.me): ${globalFng.value} · ${globalFng.classification}`}
                >
                  global <b className="font-semibold text-gray-600 dark:text-parchment-dim">{globalFng.value}</b>
                </span>
              )}
            </span>
          )}

          {/* ── chain DeFi numbers (DefiLlama) ─────────────────────────────────── */}
          {defiData && defiData.tvl > 0 && (
            <>
              <StripCell
                k="TVL"
                v={formatDefiNumber(defiData.tvl)}
                change={defiData.tvlChange24h}
                title={`${chainLabel} total value locked (DefiLlama)`}
              />
              {defiData.dexVolume24h > 0 && (
                <StripCell
                  k="DEX 24h"
                  v={formatDefiNumber(defiData.dexVolume24h)}
                  change={defiData.dexVolumeChange24h}
                  title={`DEX trading volume on ${chainLabel} (DefiLlama)`}
                />
              )}
            </>
          )}

          {/* ── breadth from the pulse's own day metrics ───────────────────────── */}
          {metrics && breadthTotal > 0 && (
            <StripCell
              k="Breadth"
              v={
                <>
                  <span className="text-signal-green dark:text-signal-green-text">{metrics.gainersCount}↑</span>{' '}
                  <span className="text-alert-red dark:text-alert-red-text">{metrics.losersCount}↓</span>
                </>
              }
              extra={metrics.neutralCount > 0 ? `${metrics.neutralCount} flat` : undefined}
              title={`24h breadth across ${breadthTotal} tracked tokens (desk pulse)`}
            />
          )}

          {defiData && defiData.stablecoinMcap > 0 && (
            <StripCell
              k="Stables"
              v={formatDefiNumber(defiData.stablecoinMcap)}
              title={`Stablecoin market cap on ${chainLabel} (DefiLlama)`}
            />
          )}
          {defiData && defiData.fees24h > 0 && (
            <StripCell
              k="Fees 24h"
              v={formatDefiNumber(defiData.fees24h)}
              change={defiData.feesChange24h}
              title="Protocol fees in last 24 hours (DefiLlama)"
            />
          )}
        </>
      )}
    </div>
  );
}
